import { useState } from 'react';
import { Search, Filter, Calendar, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ActivityLogFilters as Filters } from './types';

interface ActivityLogFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  entityTypes?: string[];
  actors?: { id: string; name: string }[];
  showEntityFilter?: boolean;
}

const ACTION_OPTIONS = [
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
];

const ENTITY_LABELS: Record<string, string> = {
  candidates: 'Candidates',
  candidate_applications: 'Applications',
  candidate_skills: 'Skills',
  candidate_education: 'Education',
  candidate_experience: 'Experience',
  candidate_projects: 'Projects',
  candidate_awards: 'Awards',
  communications: 'Communications',
  jobs: 'Jobs',
  job_stages: 'Job Stages',
  clients: 'Clients',
  companies: 'Companies',
  company_people: 'Contacts',
  skills: 'Skills Master',
};

export function ActivityLogFilters({
  filters,
  onFiltersChange,
  entityTypes = Object.keys(ENTITY_LABELS),
  actors = [],
  showEntityFilter = true,
}: ActivityLogFiltersProps) {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(filters.dateFrom);
  const [dateTo, setDateTo] = useState<Date | undefined>(filters.dateTo);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const handleActionChange = (value: string) => {
    onFiltersChange({ ...filters, action: value === 'all' ? undefined : value });
  };

  const handleEntityChange = (value: string) => {
    onFiltersChange({ ...filters, entityType: value === 'all' ? undefined : value });
  };

  const handleActorChange = (value: string) => {
    onFiltersChange({ ...filters, actorId: value === 'all' ? undefined : value });
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    onFiltersChange({ ...filters, dateFrom: date });
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    onFiltersChange({ ...filters, dateTo: date });
  };

  const clearFilters = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    onFiltersChange({});
  };

  const hasActiveFilters =
    filters.search ||
    filters.action ||
    filters.entityType ||
    filters.actorId ||
    filters.dateFrom ||
    filters.dateTo;

  const activeFilterCount = [
    filters.search,
    filters.action,
    filters.entityType,
    filters.actorId,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search activity..."
          value={filters.search || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-2">
        {/* Action Filter */}
        <Select
          value={filters.action || 'all'}
          onValueChange={handleActionChange}
        >
          <SelectTrigger className="w-[130px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {ACTION_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Entity Type Filter */}
        {showEntityFilter && (
          <Select
            value={filters.entityType || 'all'}
            onValueChange={handleEntityChange}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {entityTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {ENTITY_LABELS[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Actor Filter */}
        {actors.length > 0 && (
          <Select
            value={filters.actorId || 'all'}
            onValueChange={handleActorChange}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="User" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {actors.map((actor) => (
                <SelectItem key={actor.id} value={actor.id}>
                  {actor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Date From */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
              <Calendar className="mr-2 h-4 w-4" />
              {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={dateFrom}
              onSelect={handleDateFromChange}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Date To */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
              <Calendar className="mr-2 h-4 w-4" />
              {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={dateTo}
              onSelect={handleDateToChange}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-10"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
            <Badge variant="secondary" className="ml-1">
              {activeFilterCount}
            </Badge>
          </Button>
        )}
      </div>
    </div>
  );
}
